// Motor de INSIGHT-URI automate — 100% DETERMINIST, fără LLM.
//
// Reguli clare din date (respectă filtrele globale, via getterele din derived.ts).
// Fiecare insight e marcat ca „automat". O analiză narativă mai bogată (AI) se
// poate activa ulterior la conectarea unei chei AI — vezi nota din panou.

import {
  costInWindow,
  mediaInWindow,
  deriveProfitabilitate,
  deriveExportTrend,
  deriveTopClienti,
  deriveWaste,
  deriveLivrari,
  incarcareInWindow,
} from "@/data/derived";
import { labelCategorie } from "@/lib/labels";
import { eur, procent, numar, formatLunaKey } from "@/lib/format";

export type InsightTon = "pozitiv" | "atentie" | "negativ" | "neutru";
export type InsightTip = "Trend" | "Anomalie" | "Oportunitate" | "Risc" | "Focus";

export interface Insight {
  id: string;
  tip: InsightTip;
  ton: InsightTon;
  titlu: string;
  text: string;
}

function last<T>(a: T[]): T | undefined {
  return a.length ? a[a.length - 1] : undefined;
}
function prev<T>(a: T[]): T | undefined {
  return a.length >= 2 ? a[a.length - 2] : undefined;
}

export function computeInsights(): Insight[] {
  const out: Insight[] = [];

  // 1) Trend export % (ultima lună vs precedenta)
  const trend = deriveExportTrend();
  const tLast = last(trend);
  const tPrev = prev(trend);
  if (tLast && tPrev) {
    const delta = Math.round((tLast.export_pct_venit - tPrev.export_pct_venit) * 10) / 10;
    const semn = delta >= 0 ? "+" : "";
    out.push({
      id: "export-trend",
      tip: "Trend",
      ton: delta > 0 ? "pozitiv" : delta < 0 ? "atentie" : "neutru",
      titlu: `Export ${semn}${numar(delta, 1)}% vs. luna trecută`,
      text: `Ponderea exportului în venit a trecut de la ${procent(tPrev.export_pct_venit)} (${formatLunaKey(tPrev.luna_key)}) la ${procent(tLast.export_pct_venit)} (${formatLunaKey(tLast.luna_key)}).`,
    });
  }

  // 2) Anomalie cost /oră vs media mobilă pe 6 luni
  const media = mediaInWindow();
  const mLast = last(media);
  if (mLast && Math.abs(mLast.deviatie_pct_ma6) >= 3) {
    const peste = mLast.semnal_vs_ma6 === "PESTE_MEDIE";
    out.push({
      id: "cost-anomalie",
      tip: "Anomalie",
      ton: peste ? "negativ" : "pozitiv",
      titlu: `Cost/oră ${peste ? "peste" : "sub"} media pe 6 luni cu ${procent(Math.abs(mLast.deviatie_pct_ma6))}`,
      text: `În ${formatLunaKey(mLast.luna_key)}, costul de ${eur(mLast.cost_pe_ora_eur, 0)}/oră este ${peste ? "peste" : "sub"} MA6 (${eur(mLast.ma_6l, 0)}). ${peste ? "Verifică derapajul de cost înainte să erodeze marja." : "Cost sub trend — bun pentru marjă."}`,
    });
  }

  // 3) Categoria cu cea mai mică marjă + sugestie focus pe cea mai mare
  const cats = deriveProfitabilitate("categorie_produs").filter((c) => c.venit_eur > 0);
  if (cats.length >= 2) {
    const sortByMarja = [...cats].sort((a, b) => a.marja_pct - b.marja_pct);
    const min = sortByMarja[0];
    const max = sortByMarja[sortByMarja.length - 1];
    out.push({
      id: "focus-categorie",
      tip: "Focus",
      ton: "atentie",
      titlu: `Categoria „${labelCategorie(min.valoare)}" are marja cea mai mică (${procent(min.marja_pct)})`,
      text: `„${labelCategorie(max.valoare)}" aduce cea mai bună marjă (${procent(max.marja_pct)}). Mută efortul de vânzare spre ea sau renegociază prețul la „${labelCategorie(min.valoare)}".`,
    });
  }

  // 4) Mașină sub-utilizată → potențial de vânzări
  const masini = incarcareInWindow();
  const ultimaLuna = masini.length ? masini[masini.length - 1].luna_key : null;
  const masiniLuna = masini.filter((m) => m.luna_key === ultimaLuna);
  if (masiniLuna.length) {
    const min = [...masiniLuna].sort((a, b) => a.incarcare_pct - b.incarcare_pct)[0];
    if (min.incarcare_pct < 50) {
      out.push({
        id: "masina-capacitate",
        tip: "Oportunitate",
        ton: "atentie",
        titlu: `„${min.nume_masina}" la ${procent(min.incarcare_pct)} din capacitate`,
        text: `În ${formatLunaKey(min.luna_key)}, mașina e departe de capacitatea teoretică. Capacitate liberă = potențial de vânzări suplimentare fără investiție nouă.`,
      });
    }
  }

  // 5) Concentrare pe top client (risc)
  const clienti = deriveTopClienti();
  const topClient = clienti[0];
  if (topClient && topClient.pondere_venit_pct >= 20) {
    out.push({
      id: "concentrare-client",
      tip: "Risc",
      ton: "negativ",
      titlu: `„${topClient.client}" = ${numar(topClient.pondere_venit_pct, 1)}% din venit`,
      text: `Dependență ridicată de un singur client. Diversifică portofoliul ca să reduci riscul dacă acest client scade comenzile.`,
    });
  }

  // 6) Pierdere / waste — operația cu cel mai mare cost
  const waste = deriveWaste();
  const opMax = waste.peOperatie[0];
  if (opMax && waste.totalCost > 0) {
    out.push({
      id: "waste-operatie",
      tip: "Risc",
      ton: opMax.pierdere_pct >= 6 ? "negativ" : "atentie",
      titlu: `Pierdere maximă la „${opMax.operatie}" — ${eur(opMax.cost_pierdere_eur, 0)}`,
      text: `Rata de rebut la ${opMax.operatie} e ${procent(opMax.pierdere_pct)}. Total pierderi pe perioadă: ${eur(waste.totalCost, 0)}. O reducere de 1pp aici ar recupera vizibil din marjă.`,
    });
  }

  // 7) Livrări — dacă „la timp %" e sub prag
  const livrari = deriveLivrari();
  if (livrari.totalComenzi > 0) {
    const bun = livrari.procentLaTimp >= 85;
    out.push({
      id: "livrari",
      tip: bun ? "Trend" : "Risc",
      ton: bun ? "pozitiv" : "negativ",
      titlu: `Livrări la timp: ${procent(livrari.procentLaTimp)}`,
      text: bun
        ? `${livrari.laTimp} din ${livrari.totalComenzi} comenzi respectă termenul promis. Întârziere medie ${numar(livrari.intarziereMedieZile, 1)} zile.`
        : `Doar ${livrari.laTimp} din ${livrari.totalComenzi} comenzi ies la timp (întârziere medie ${numar(livrari.intarziereMedieZile, 1)} zile). Verifică clienții/lunile cu întârzieri în secțiunea Livrări.`,
    });
  }

  // 8) Marjă medie generală (context)
  const totalVenit = cats.reduce((s, c) => s + c.venit_eur, 0);
  const totalMarja = cats.reduce((s, c) => s + c.marja_eur, 0);
  if (totalVenit > 0) {
    const marjaPct = (totalMarja / totalVenit) * 100;
    const bun = marjaPct >= 28;
    out.push({
      id: "marja-generala",
      tip: "Trend",
      ton: bun ? "pozitiv" : "atentie",
      titlu: `Marjă medie ${procent(marjaPct)} pe comenzile filtrate`,
      text: `Venit ${eur(totalVenit, 0)}, marjă ${eur(totalMarja, 0)}. ${bun ? "Peste pragul sănătos de 28%." : "Sub 28% — atenție la structura de cost și la prețuri."}`,
    });
  }

  // Cost /oră ultima valoare (context util dacă nu s-a declanșat anomalia)
  const cost = costInWindow();
  const cLast = last(cost);
  if (cLast && !out.some((i) => i.id === "cost-anomalie")) {
    out.push({
      id: "cost-context",
      tip: "Trend",
      ton: "neutru",
      titlu: `Cost/oră stabil la ${eur(cLast.cost_pe_ora_eur, 0)}`,
      text: `În ${formatLunaKey(cLast.luna_key)}, costul real /oră e aproape de trend (reper istoric ~200 €/oră).`,
    });
  }

  return out;
}
